import { HexBigInt } from 'dex-app.cm';

import { Currency } from '@/constants';
import Big from '@/utils/Bigjs';

export const bigIntToDecimals18String = (value: bigint) => {
  const decString = value.toString(10).padStart(19, '0');

  return `${decString.slice(0, -18)}.${decString.slice(-18)}`.replace(/\.?0+$/, '');
};

export const intToHexString = (d: number | null, padding: number): HexBigInt => {
  let hex = Number(d).toString(16);

  while (hex.length < padding) {
    hex = `0${hex}`;
  }

  return `0x${hex}`;
};

export function eFix(number: string | number) {
  const formattedValue = String(number).toLowerCase();
  const valueHasExponentIndex = formattedValue.indexOf('e');

  if (valueHasExponentIndex !== -1) {
    const enNumber = new Intl.NumberFormat('en', { notation: 'compact' }).format(Number(number));

    return enNumber.toString();
  } else {
    return number.toString();
  }
}

function getNewRound(number: number, lenNumber: number, decRound: number, percentRound: number) {
  if (lenNumber < decRound) return decRound;
  const newNumber: number = Number(number.toFixed(lenNumber));
  const percent100: number = 100;
  const percent = (newNumber / number) * percent100 - percent100;

  if (Math.abs(percent) <= percentRound) {
    lenNumber = getNewRound(newNumber, lenNumber - 1, decRound, percentRound);
  } else {
    lenNumber += 1;
  }

  return lenNumber;
}

/**
 * Parses a string or number to a Big number and sets the maximum precision to +/-30
 * to avoid scientific notation (e-notation) and returns a Big number with toNumber and toString methods
 * that remove trailing zeroes and allow proper number representation
 */
export const parseFloatGuarded = (value: string | number): Big => {
  let guardedNumber: Big;
  try {
    guardedNumber = new Big(value);
  } catch {
    guardedNumber = new Big(0);
  }

  guardedNumber.toString = function (): string {
    const strValue = this.toFixed();
    return strValue.replace(/\.0{1,}$/, '');
  };

  return guardedNumber;
};

export function formatNumberWithPrecision(number: string | number, decRound: number, percentRound: number = 1): string {
  if (!number) {
    return '0';
  }

  const numericValue = Number(number);

  if (numericValue === 0) {
    return '0';
  }

  const fixedNumber = numericValue.toFixed(decRound); // round to decRound

  const trimmedNumber = parseFloatGuarded(fixedNumber).toString(); // trailing zeroes are removed

  const doubleNumber = trimmedNumber.split('.');

  if (doubleNumber.length < 2) {
    return trimmedNumber;
  }

  const lenNumber = doubleNumber[1].length;
  const roundNumber = getNewRound(numericValue, lenNumber, decRound, percentRound);

  if (roundNumber > lenNumber) {
    return trimmedNumber;
  }

  const newNumber = trimmedNumber.substr(0, doubleNumber[0].length + roundNumber + 1);

  return newNumber;
}

/**
 * This function returns a string as we need exact precision.
 * Do not convert it to JS number, it will lose precision.
 * Use Big.js instead every time this ratio is going to be used!
 */
export function calculateRatioFromBigInts(amount1: bigint, amount2: bigint): string {
  if (amount2 === 0n) {
    return '0';
  }

  return Big(amount1.toString()).div(amount2.toString()).toString();
}

export function calculateAmountFromRatio(amount: bigint, ratio: Big | string | number): bigint {
  const result = Big(amount.toString()).mul(ratio);
  return BigInt(result.toFixed(0));
}

type BigIntHexString = string;

export function decimalsForCurrency(currency: Currency): number {
  switch (currency) {
    case Currency.TRX:
    case Currency.USDT_TRX:
    case Currency.USDC_ETH:
    case Currency.USDT_POL:
    case Currency.USDC_POL:
    case Currency.USDT_ETH:
      return 6;
    case Currency.WBTC_BNB:
    case Currency.WBTC_ETH:
    case Currency.LTC:
    case Currency.DOGE:
    case Currency.BTC:
      return 8;
    case Currency.USDT_BNB:
    case Currency.ETH:
    case Currency.BNB:
    case Currency.CWEB:
    case Currency.WCWEB_ETH:
    case Currency.USDC_BNB:
    case Currency.USD1_ETH:
    case Currency.USD1_BNB:
    case Currency.POL:
      return 18;
    default:
      return 18;
  }
}

export function convertStringToBigInt(value: string, currency: Currency): bigint {
  const decimals = decimalsForCurrency(currency);
  const [integerPart, decimalPart = ''] = Big(value).toString().split('.');
  const decimalPartPadded = (decimalPart + '0'.repeat(decimals)).slice(0, decimals); // Ensure correct decimal places

  return BigInt(integerPart.concat(decimalPartPadded));
}

export function convertBigIntToString(
  value: bigint | BigIntHexString,
  currency: Currency,
  roundedTo?: number,
  removeTrailingZeros = false
): string {
  const decimals = decimalsForCurrency(currency);
  const valueBig = Big(value.toString());
  const divisor = Big(10).pow(decimals);

  if (roundedTo !== undefined && roundedTo >= 0) {
    const result = valueBig.div(divisor).toFixed(roundedTo);
    return result;
  }

  const result = valueBig.div(divisor).toFixed(decimals);

  if (removeTrailingZeros) {
    return Big(result).toString();
  }

  return result;
}

/**
 * Reduces the amount of decimals based on the length of the integer part.
 * Max precision = decimalsLengthBoundary
 * If integer part is > decimalsLengthBoundary, precision = 0
 *
 * IMPORTANT: Receives the bigint! amount of a token, if currency provided!
 */
export const calculatePrecisionNumberStats = (
  amount: bigint | number,
  currency: Currency | null = null,
  decimalsLengthBoundary = 6
) => {
  const withDecimals = currency ? Number(amount) / 10 ** decimalsForCurrency(currency) : Number(amount);

  const [integer, decimals = ''] = parseFloatGuarded(withDecimals).toString().split('.');

  const integerLength = integer.length;
  const decimalsLength = decimals.length;

  const precision =
    integerLength <= 1
      ? decimalsLengthBoundary
      : integerLength > decimalsLengthBoundary
        ? 0
        : decimalsLengthBoundary - integerLength;

  const withPrecision = [integer, decimals.slice(0, precision)].filter(Boolean).join('.');

  return {
    integer: parseFloatGuarded(integer).toString(),
    integerLength,
    decimals: decimalsLength > 0 ? parseFloatGuarded(decimals).toString() : '',
    decimalsLength,
    amount: parseFloatGuarded(Number(amount)).toString(),
    amountWithDecimals: parseFloatGuarded(withDecimals).toString(),
    amountWithPrecision: parseFloatGuarded(withPrecision).toString(),
  };
};

export const humanReadableStringFromTokenBigInt = (amount: bigint, token: Currency, precision = 10) => {
  return formatNumberWithPrecision(convertBigIntToString(amount, token), precision);
};

export const addThousandsSeparator = (value: string, separator: '_' | ',' | '/' | ' ' = ','): string => {
  if (value === '') {
    return value;
  }

  if (Number(value) === 0) {
    return value;
  }

  const [integer, decimals = ''] = String(value).split('.');
  const integerWithSeparator = integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return decimals
    ? `${integerWithSeparator}.${decimals}`
    : value.endsWith('.')
      ? `${integerWithSeparator}.`
      : integerWithSeparator;
};

export const removeThousandsSeparator = (value: string, separator: '_' | ',' | '/' | ' ' = ','): string => {
  const escapedSeparator = separator.replace(/[\\]/g, '\\$&');
  const regex = new RegExp(escapedSeparator, 'g');
  return value.replace(regex, '');
};
